import { useState, useEffect, useRef } from "react";
import "./LandingPage.css";
import SiteHeader, { SiteHeaderAccountActions } from "./SiteHeader";
import { CONTACT } from "./plans";

// Hauteurs de barres fixes : un code-barres qui change à chaque rendu
// donnerait une carte "vivante" de façon incohérente.
const BARCODE = [22, 12, 28, 16, 24, 10, 30, 18, 14, 26, 12, 22, 30, 16, 10, 24, 18, 28, 12, 20, 26, 14, 22, 16];

const STAMPS_FILLED = 7;

// Les 3 étapes de mise en place, présentées en carrousel, chacune illustrée
// par une capture réelle du produit.
const STEPS = [
  {
    title: "Créez votre espace",
    text: "Sélectionnez une offre, inscrivez votre commerce et personnalisez votre carte de fidélité.",
    image: "/etape-1.png",
    alt: "Les trois offres Fidelity Card avec leurs tarifs mensuels et annuels.",
  },
  {
    title: "Affichez votre QR",
    text: "Imprimez ou affichez le QR code dans votre boutique pour que vos clients s'inscrivent.",
    image: "/etape-2.png",
    alt: "Affiche de fidélité en boutique : le QR code d'inscription et les 3 étapes pour le client.",
  },
  {
    title: "Fidélisez",
    text: "Scannez les cartes à chaque passage pour créditer des points. C'est tout.",
    image: "/etape-3.png",
    alt: "Écran de scan d'une carte client, avec le bouton Ajouter un tampon.",
  },
];

// Cadence du défilement automatique. Assez lent pour lire l'étape et sa
// capture, assez vif pour montrer qu'il y a une suite. À garder en phase avec
// l'animation landing-dot-progress (LandingPage.css).
const AUTOPLAY_MS = 6000;

function LandingPage({ user, onGoLogin, onGoStart, onGoGestion, onLogout, onMenuNavigate }) {
  const [step, setStep] = useState(0);
  // CTA fixe mobile : apparaît une fois le premier écran (et son gros
  // bouton) dépassé, pour garder l'accès aux offres à portée de pouce.
  const [showStickyCta, setShowStickyCta] = useState(false);
  useEffect(() => {
    const onScroll = () =>
      setShowStickyCta(window.scrollY > window.innerHeight * 0.7);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  // Le carrousel avance seul, mais s'efface dès que le visiteur interagit :
  // survol/focus le suspendent, une action manuelle repart le compteur à zéro.
  const [paused, setPaused] = useState(false);
  const [inView, setInView] = useState(false);
  const [reducedMotion] = useState(() =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const carouselRef = useRef(null);
  const touchStartX = useRef(null);
  // Modulo pour que les flèches bouclent au lieu de se désactiver aux extrémités.
  const goStep = (i) => setStep((i + STEPS.length) % STEPS.length);

  // Hors écran, inutile d'animer : le défilement ne tourne que lorsque la
  // section est réellement visible.
  useEffect(() => {
    const node = carouselRef.current;
    if (!node || !("IntersectionObserver" in window)) {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.4 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const autoplay = inView && !paused && !reducedMotion;

  // Relancé à chaque changement d'étape : une navigation manuelle remet donc
  // le compteur à zéro au lieu d'enchaîner aussitôt sur l'étape suivante.
  useEffect(() => {
    if (!autoplay) return;
    const timer = setTimeout(
      () => setStep((s) => (s + 1) % STEPS.length), AUTOPLAY_MS);
    return () => clearTimeout(timer);
  }, [autoplay, step]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    // Seuil : distinguer un balayage volontaire d'un doigt qui se pose
    if (Math.abs(delta) > 40) goStep(step + (delta < 0 ? 1 : -1));
  };
  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") { e.preventDefault(); goStep(step - 1); }
    if (e.key === "ArrowRight") { e.preventDefault(); goStep(step + 1); }
  };

  return (
    <div className="landing">
      <SiteHeader position="fixed">
        <SiteHeaderAccountActions
          user={user}
          onGoLogin={onGoLogin}
          onGoGestion={onGoGestion}
          onLogout={onLogout}
          onMenuNavigate={onMenuNavigate}
        />
      </SiteHeader>

      <section className="landing-hero">
        <div className="landing-hero-content">
          <p className="rc-eyebrow">Sans engagement caché, un prix clair</p>
          <h1>
            Plantez votre programme de fidélité,
            <br />
            <em>et faites revenir vos clients.</em>
          </h1>
          <p className="landing-hero-sub">
            Une carte de fidélité qui vit dans le téléphone de vos clients —
            sans application, sans carte physique perdue. Choisissez votre
            formule, on s'occupe du reste.
          </p>
          <div className="landing-hero-actions">
            <button className="rc-btn rc-btn-terracotta landing-btn-hero" onClick={onGoStart}>
              Découvrir nos offres
              <span className="landing-btn-hero-arrow" aria-hidden="true">→</span>
            </button>
            <a href="#features" className="landing-btn-ghost">
              En savoir plus
              <span className="landing-btn-arrow">↓</span>
            </a>
          </div>
        </div>
        <div className="landing-hero-visual">
          <div className="landing-card-mock">
            <div className="landing-card-mock-header">
              <div className="landing-card-mock-logo">V</div>
              <div>
                <div className="landing-card-mock-name">Votre commerce</div>
                <div className="landing-card-mock-type">Carte de fidélité</div>
              </div>
            </div>
            <div className="landing-card-mock-stamps">
              {Array.from({ length: 10 }).map((_, i) => (
                <div
                  key={i}
                  className={`landing-card-mock-stamp${i < STAMPS_FILLED ? " is-filled" : ""}`}
                >
                  {i < STAMPS_FILLED && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
              ))}
            </div>
            <div className="landing-card-mock-caption">
              <span>7 tampons</span>
              <span>plus que 3 avant la récompense</span>
            </div>
            <div className="landing-card-mock-barcode">
              {BARCODE.map((h, i) => (
                <div key={i} className="landing-card-mock-bar" style={{ height: h }} />
              ))}
            </div>
          </div>
          <div className="landing-hero-glow" />
        </div>
      </section>

      <section className="landing-features" id="features">
        <div className="landing-section-inner">
          <p className="rc-eyebrow landing-eyebrow-center">La Fidelity Card</p>
          <h2 className="landing-section-title">
            Tout ce qu'il faut pour faire revenir vos clients
          </h2>
          <p className="landing-section-sub">
            Une plateforme complète pour gérer vos cartes de fidélité
            numériques, cultivée pour les commerces de proximité.
          </p>
          <div className="landing-features-grid">
            <div className="landing-feature-card">
              <div className="landing-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M3 9h18" />
                  <path d="M9 3v18" />
                  <path d="M15 3v18" />
                  <path d="M3 15h18" />
                </svg>
              </div>
              <h3>QR codes instantanés</h3>
              <p>
                Un QR code au comptoir : vos clients scannent et s'inscrivent
                en quelques secondes, sans application.
              </p>
            </div>
            <div className="landing-feature-card">
              <div className="landing-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="18" rx="3" />
                  <path d="M2 9h20" />
                  <path d="M9 15h6" />
                </svg>
              </div>
              <h3>Cartes dématérialisées</h3>
              <p>
                Fini le plastique. Vos clients ajoutent leur carte directement
                dans Google Wallet ou Apple Wallet.
              </p>
            </div>
            <div className="landing-feature-card">
              <div className="landing-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
                </svg>
              </div>
              <h3>À l'image de votre commerce</h3>
              <p>
                Logo, couleurs, tampons ou points : chaque carte porte
                l'identité visuelle de votre boutique.
              </p>
            </div>
            <div className="landing-feature-card">
              <div className="landing-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20V10" />
                  <path d="M18 20V4" />
                  <path d="M6 20v-4" />
                </svg>
              </div>
              <h3>Suivi en temps réel</h3>
              <p>
                Tableau de bord complet : points distribués, clients actifs,
                tendances. Pilotez votre fidélisation.
              </p>
            </div>
            <div className="landing-feature-card">
              <div className="landing-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="m2 7 10 6 10-6" />
                </svg>
              </div>
              <h3>E-mails et SMS automatiques</h3>
              <p>
                Déclenchés selon vos règles : anniversaire, récompense à portée,
                client qui s'éloigne. Vos clients reviennent sans que vous y pensiez.
              </p>
            </div>
            <div className="landing-feature-card">
              <div className="landing-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 10v4a1 1 0 0 0 1 1h3l5 4V5L7 9H4a1 1 0 0 0-1 1Z" />
                  <path d="M16 9.5a3.5 3.5 0 0 1 0 5" />
                  <path d="M19 7a7 7 0 0 1 0 10" />
                </svg>
              </div>
              <h3>Campagnes de publicité</h3>
              <p>
                Fêtes, soldes, rentrée : lancez vos campagnes sur les périodes
                importantes et touchez vos clients au bon moment.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-steps">
        <div className="landing-section-inner">
          <h2 className="landing-section-title">En place en 3 étapes</h2>
          <p className="landing-section-sub">
            Pas de configuration complexe. On plante, ça pousse.
          </p>
          <div className="landing-steps-cta">
            <button className="rc-btn rc-btn-cream landing-btn-lg" onClick={onGoStart}>
              Découvrir nos offres
            </button>
          </div>
          <div
            className="landing-steps-carousel"
            ref={carouselRef}
            role="region"
            aria-roledescription="carrousel"
            aria-label="Les 3 étapes de mise en place"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            onKeyDown={onKeyDown}
          >
            <button
              type="button"
              className="landing-steps-nav"
              onClick={() => goStep(step - 1)}
              aria-label="Étape précédente"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div className="landing-steps-viewport">
              <div
                className="landing-steps-track"
                style={{ transform: `translateX(-${step * 100}%)` }}
              >
                {STEPS.map((s, i) => (
                  <div
                    className={`landing-step-slide${i === step ? " is-active" : ""}`}
                    key={s.title}
                    aria-hidden={i !== step}
                    role="group"
                    aria-roledescription="diapositive"
                    aria-label={`Étape ${i + 1} sur ${STEPS.length}`}
                  >
                    <div className="landing-step">
                      <div className="landing-step-number">{i + 1}</div>
                      <h3>{s.title}</h3>
                      <p>{s.text}</p>
                    </div>
                    {s.image && (
                      <div className="landing-step-visual">
                        {/* Pas de loading="lazy" : le défilement automatique
                            afficherait sinon une diapositive encore vide. */}
                        <img src={s.image} alt={s.alt} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <button
              type="button"
              className="landing-steps-nav"
              onClick={() => goStep(step + 1)}
              aria-label="Étape suivante"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
          <div className="landing-steps-dots">
            {STEPS.map((s, i) => (
              <button
                type="button"
                key={`${s.title}-${step === i ? step : "idle"}`}
                className={`landing-steps-dot${i === step ? " is-active" : ""}${autoplay ? " is-running" : ""}`}
                onClick={() => goStep(i)}
                aria-label={`Étape ${i + 1} : ${s.title}`}
                aria-current={i === step}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="landing-cta-section">
        <div className="landing-section-inner">
          <p className="rc-eyebrow landing-eyebrow-center">Plantons votre projet</p>
          <h2>Prêt à faire revenir vos clients ?</h2>
          <button className="rc-btn rc-btn-terracotta landing-btn-lg" onClick={onGoStart}>
            Voir les offres
          </button>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-footer-left">
            <span className="rc-wordmark landing-footer-brand">Racines</span>
            <span className="landing-footer-copy">
              © {new Date().getFullYear()} Racines · Cartes de fidélité
            </span>
          </div>
          <div className="landing-footer-contact">
            <a href={`tel:${CONTACT.phone.replace(/ /g, "")}`}>{CONTACT.phone}</a>
            <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>
            <a href={`https://${CONTACT.site}`} target="_blank" rel="noopener noreferrer">
              {CONTACT.site}
            </a>
          </div>
        </div>
        <div className="landing-footer-legal">
          <a href="/mentions-legales">Mentions légales</a>
          <a href="/confidentialite">Politique de confidentialité</a>
          <a href="/cgv">CGV</a>
        </div>
      </footer>

      {/* CTA fixe bas d'écran, mobile uniquement (masqué en CSS au-delà) */}
      <div className={`landing-sticky-cta ${showStickyCta ? "is-visible" : ""}`}>
        <button className="rc-btn rc-btn-terracotta" onClick={onGoStart}>
          Découvrir nos offres
        </button>
      </div>
    </div>
  );
}

export default LandingPage;
